import { useTranslation } from 'react-i18next'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Flex, Form, Layout, MenuProps, Select, Space, Typography } from 'antd'
import { colorBorder, colorText } from 'src/app/themes/primaryThemeDefault'
import { CopyIcon, PenIcon, RefreshIcon, WhiteSearchIcon } from 'src/assets/icons'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import dayjs from 'dayjs'
import { IReqClientUserPayload, useClientSelectData, useClientUserSetup } from './useClientUserSetup'
import { StatusTag, YesNoTag } from '../../../feature-business-administration/bank-user-setup/components/TagComponents'
import { FORMAT_DATE_TIME } from '../../../../shared/constants/app.constants'
import { getClientUserList } from '../../../../shared/services/ClientUserSetupService'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import './index.scss'
import { ShortenListTags } from '../../../../shared/components/common/tag/ShortenListTags'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { settingHeightTableWithFilter } from 'src/app/shared/constants/common.constants'
import { useClientUserSetupFilters } from '../ClientUserSetupFiltersContext'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'

const ClientSetup: React.FC = () => {
  const { t } = useTranslation(undefined, { useSuspense: true })
  const navigate = useNavigate()
  const { userPermissions } = useRolePermission()
  const { clientUserList, totalRecords, isLoading, getClientUserListData } = useClientUserSetup()
  const { clientOptions, getClientOptions } = useClientSelectData()
  const { saveSearchParams, restoreSearchParams } = useClientUserSetupFilters()
  const currentPayload = useRef<IReqClientUserPayload>()
  const [isSearchDropdown, setIsSearchDropdown] = useState<boolean>(false)
  
  const [defaultParams] = useState(() => {
    const restoredParams = restoreSearchParams()
    return restoredParams || null
  })
  
  const [clientName, setClientName] = useState<string>(() => {
    return defaultParams?.filterId || ''
  })

  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`ClientUserSetup:list.tableColumns.${field}`)
    },
    [t],
  )

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.CLIENT_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t(`ClientUserSetup:list.label.editClientUser`)}
                </Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.editClientUserSetup}/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.CLIENT_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorText }}>
                  {t(`ClientUserSetup:list.label.copyClientUser`)}
                </Typography.Text>
              ),
              icon: <CopyIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.copyClientUserSetup}/${rowData?.id}`)
              },
            }
          : null,
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps

    [userPermissions, t, navigate],
  )

  const generateCustomColumnDefs = useCallback(
    (colDefs: Array<TableColumnDefs>): Array<TableColumnDefs> =>
      colDefs.map((item, index) => {
        const { colId } = item
        if (colId === 'status')
          return {
            ...item,
            dataType: 'date',
            cellRenderer: (props: any) => {
              const { data } = props
              return <StatusTag status={data.status} />
            },
          } as any

        if (colId === 'doNotSendEmailToThisAddress')
          return {
            ...item,
            dataType: 'date',
            cellRenderer: (props: any) => {
              const { data } = props
              return <YesNoTag status={data.doNotSendEmailToThisAddress} />
            },
          } as any

        if (colId === 'userRoles')
          return {
            ...item,
            headerClass: 'custom-header-text',
            cellRenderer: (props: any) => {
              const { data } = props
              const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
              return data.userRoles ? <ShortenListTags data={data.userRoles} width={columnWidth} /> : null
            },
          }

        if (colId === 'accountNames')
          return {
            ...item,
            cellRenderer: (props: any) => {
              const { data } = props
              const columnWidth = props.eGridCell ? props.eGridCell.clientWidth : props.column.getActualWidth()
              return data.accountNames ? <ShortenListTags data={data.accountNames} width={columnWidth} /> : null
            },
          }

        if (colId === 'lastLoggedOn')
          return {
            ...item,
            dataType: 'date',
            cellRenderer: (props: any) => {
              const { data } = props
              return data.lastLoggedOn ? dayjs(data.lastLoggedOn).format(FORMAT_DATE_TIME) : ''
            },
          }

        if (colId === 'client')
          return {
            ...item,
            isHideInSearchSelect: true,
          }

        if (['primaryPhone', 'mobilePhone'].includes(colId)) {
          item.dataType = 'number'
        }

        return item
      }),
    [],
  )

  const handleAddRecord = () => {
    navigate(menuUrls.addClientUserSetup)
  }

  const handleSearch = useCallback(async (payload: Partial<IReqClientUserPayload>) => {
    if (currentPayload.current?.filterId) {
      payload.filterId = currentPayload.current?.filterId
    }
    currentPayload.current = payload

    const searchColumns = payload.searchColumns?.filter((item) => item !== 'client')
    payload.searchColumns = searchColumns
    const paramsToSave = {
      ...payload,
      isSearchAllColumns: (payload.searchColumns?.length || 0) > 1,
    }
    saveSearchParams(paramsToSave)

    await getClientUserListData(payload)
    setIsSearchDropdown(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveSearchParams])

  const staticOption = useCallback(() => {
    return {
      label: t(`ClientUserSetup:list.label.allClient`),
      value: '',
    }
  }, [t])
  const filterOption = (input: string, option?: { label: string; value: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const handleClientNameChange = (value: string) => {
    if (currentPayload.current) {
      setClientName(value)
      currentPayload.current.filterId = value
      currentPayload.current.pageIndex = 1
    }
  }

  const handleClickSearchByClient = () => {
    if (currentPayload.current) {
      setIsSearchDropdown(true)
      currentPayload.current.searchTerm = ''
      if (!currentPayload.current?.filterId) {
        delete currentPayload.current?.filterId
      }
      handleSearch(currentPayload.current)
    }
  }

  const handleClickRefreshClientSelect = () => {
    setClientName(staticOption().value)
    if (currentPayload.current) {
      setIsSearchDropdown(true)
      delete currentPayload.current.filterId
      currentPayload.current.searchTerm = ''
      currentPayload.current.pageIndex = 1
      handleSearch(currentPayload.current)
    }
  }

  useEffect(() => {
    getClientOptions()
  }, [])

  // Restore saved search params when component mounts
  useEffect(() => {
    const savedParams = restoreSearchParams()
    
    if (savedParams) {
      const { isSearchAllColumns, searchTerm } = savedParams
      setTimeout(() => {
        const formSearchColumns = isSearchAllColumns ? 'All' : (savedParams.searchColumns?.[0] || 'All')
        
        outerTableRef.current?.setSearchFormValues?.(
          searchTerm || '',
          formSearchColumns
        )
        if (searchTerm) {
          outerTableRef.current?.setSearchOpen?.(true)
        }
      }, 300)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Flex className='landing-client-user-setup'>
      <Layout.Content style={{ padding: 24 }}>
        <Form className='form-container' layout='vertical' style={{ padding: 0 }}>
          <Space size={12}>
            <Form.Item label={t('ClientUserSetup:clientName.title')}>
              <Select
                style={{ width: 332 }}
                showSearch
                optionFilterProp='children'
                filterOption={filterOption}
                options={[staticOption(), ...clientOptions]}
                defaultValue={''}
                onChange={handleClientNameChange}
                value={clientName}
                disabled={isLoading}
              />
            </Form.Item>

            <Button
              id='search-button'
              icon={<WhiteSearchIcon />}
              type={'primary'}
              size={'large'}
              style={{ marginLeft: 12 }}
              onClick={handleClickSearchByClient}
              disabled={isLoading}
            />
            <Button
              id='search-button'
              icon={<RefreshIcon />}
              size={'large'}
              style={{ border: `1px solid ${colorBorder}` }}
              onClick={handleClickRefreshClientSelect}
              disabled={isLoading}
            />
          </Space>
        </Form>

        <NewCommonTable
          ref={outerTableRef}
          loading={isLoading}
          rowData={clientUserList}
          total={totalRecords}
          displayAddBtn={userPermissions[ESubModules.CLIENT_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
          displayActionColumn
          pageName={TABLE_NAME.CLIENT_USER_SETUP}
          tableSettingName={TABLE_NAME.CLIENT_USER_SETUP}
          translationNamespace='ClientUserSetup:list.tableColumns'
          handleAddRecord={handleAddRecord}
          actionDropdownItems={getItems}
          handleSearch={handleSearch as any}
          handleDownload={async (payload) => await getClientUserList(payload as any)}
          getHeaderName={getHeaderName}
          generateCustomColumnDefs={generateCustomColumnDefs}
          isSearchDropdown={isSearchDropdown}
          settingHeightTable={settingHeightTableWithFilter}
          defaultParams={defaultParams}
        />
      </Layout.Content>
    </Flex>
  )
}

export default ClientSetup
